import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check, Circle } from 'lucide-react';

const roadmapPhases = [
  {
    phase: 'Phase 1',
    title: 'Foundation',
    status: 'completed',
    items: [
      'Token creation & smart contract deployment',
      'Brand identity & website launch',
      'Community building on Telegram & X',
      'Initial marketing campaigns',
    ],
  },
  {
    phase: 'Phase 2',
    title: 'Launch & Growth',
    status: 'active',
    items: [
      'PancakeSwap listing',
      'CoinGecko & CoinMarketCap listings',
      'Influencer partnerships',
      'Holder milestone rewards',
    ],
  },
  {
    phase: 'Phase 3',
    title: 'Ecosystem Expansion',
    status: 'active',
    items: [
      'Staking platform launch',
      'Strategic partnerships',
      'Cross-chain bridge development',
      'Mobile app development',
    ],
  },
  {
    phase: 'Phase 4',
    title: 'Exchange Live',
    status: 'completed',
    items: [
      'BITZX Exchange live at exchange.bitzx.io',
      'KYC/AML integration',
      'INR deposit & payout flows',
      'Advanced trading features',
    ],
  },
  {
    phase: 'Phase 5',
    title: 'Full Ecosystem',
    status: 'upcoming',
    items: [
      'Options & advanced products',
      'Global expansion',
      'Institutional trading desk',
      'Continuous innovation & growth',
    ],
  },
];

export const Roadmap = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'active':
        return 'bg-[#EBD38D]';
      default:
        return 'bg-[#4A4B50]';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <Check size={16} className="text-white" />;
      case 'active':
        return <Circle size={16} className="text-[#1B1E20] fill-current" />;
      default:
        return <Circle size={16} className="text-[#8A8B90]" />;
    }
  };

  return (
    <section
      id="roadmap"
      ref={ref}
      className="py-24 md:py-32 xl:py-36 relative overflow-hidden bg-[#0a0b0d]"
      data-testid="roadmap-section"
    >
      {/* Background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#9C7941]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#EBD38D]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 xl:px-16 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[#EBD38D] text-sm font-semibold tracking-wider uppercase mb-4">
            Roadmap
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Our Journey to Success
          </h2>
          <p className="text-[#D5D5D0] text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            A clear path from token launch to building a comprehensive crypto ecosystem
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line (Desktop) */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-px h-full bg-gradient-to-b from-[#9C7941] via-[#EBD38D] to-[#4A4B50]" />

          {/* Phases */}
          <div className="space-y-12 lg:space-y-16">
            {roadmapPhases.map((phase, index) => (
              <motion.div
                key={phase.phase}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className={`relative lg:grid lg:grid-cols-2 lg:gap-12 ${
                  index % 2 === 0 ? '' : 'lg:flex-row-reverse'
                }`}
                data-testid={`roadmap-phase-${index}`}
              >
                {/* Content */}
                <div className={`${index % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:col-start-2 lg:pl-12'}`}>
                  <div className={`card-glass p-6 md:p-8 ${phase.status === 'active' ? 'border-[#EBD38D]/50' : ''}`}>
                    {/* Phase Badge */}
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${getStatusColor(phase.status)}`}>
                        {getStatusIcon(phase.status)}
                      </span>
                      <span className="text-[#EBD38D] text-sm font-semibold">{phase.phase}</span>
                      {phase.status === 'active' && (
                        <span className="text-xs px-2 py-1 bg-[#EBD38D]/10 text-[#EBD38D] rounded-full">
                          In Progress
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4">{phase.title}</h3>

                    {/* Items */}
                    <ul className="space-y-3">
                      {phase.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-3 text-left">
                          <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${
                            phase.status === 'completed' ? 'bg-green-500' :
                            phase.status === 'active' ? 'bg-[#EBD38D]' : 'bg-[#4A4B50]'
                          }`} />
                          <span className={`text-sm ${
                            phase.status === 'completed' ? 'text-[#8A8B90] line-through' :
                            phase.status === 'active' ? 'text-[#D5D5D0]' : 'text-[#8A8B90]'
                          }`}>
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Timeline Node (Desktop) */}
                <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 top-8">
                  <div className={`w-6 h-6 rounded-full border-4 border-[#1B1E20] ${getStatusColor(phase.status)} ${
                    phase.status === 'active' ? 'animate-pulse-glow' : ''
                  }`} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-10 text-center"
        >
          <p className="text-[#6B6D75] text-sm">
            Roadmap is subject to change based on market conditions and community feedback
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Roadmap;
